import { useEffect, useState } from "react";
import { Button, Card, Input, Space, Tag, Text, Tooltip, useToast } from "@app/ui";
import { updatePassportMarketDates } from "../api/dpp.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { MetaRow } from "./MetaRow.js";
import type { PassportDetail } from "../types.js";

const day = (iso: string | null) => iso?.slice(0, 10) ?? "";

/**
 * Retention clock + backup copy (draft TR ESPR Art. 11(3)(ğ), 12(5), 27(9)):
 * the market dates decide how long the passport must stay online; the backup
 * row proves a copy exists at the independent host. Owns its date drafts.
 */
export function PassportRetentionCard({
  orgId,
  passport,
  canWrite,
  onSaved,
  onError,
}: {
  orgId: string;
  passport: PassportDetail;
  canWrite: boolean;
  onSaved: () => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const toast = useToast();
  const fmtDate = useFormatDateTime();
  const { retention, backup } = passport;
  // YYYY-MM-DD drafts; blank = clear.
  const [placedOn, setPlacedOn] = useState(day(retention.placedOnMarketAt));
  const [lastUnit, setLastUnit] = useState(day(retention.lastUnitPlacedOnMarketAt));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPlacedOn(day(retention.placedOnMarketAt));
    setLastUnit(day(retention.lastUnitPlacedOnMarketAt));
  }, [retention.placedOnMarketAt, retention.lastUnitPlacedOnMarketAt]);

  const dirty =
    placedOn !== day(retention.placedOnMarketAt) || lastUnit !== day(retention.lastUnitPlacedOnMarketAt);

  const save = async () => {
    setSaving(true);
    try {
      await updatePassportMarketDates(orgId, passport.id, {
        placedOnMarketAt: placedOn.trim() === "" ? null : placedOn.trim(),
        lastUnitPlacedOnMarketAt: lastUnit.trim() === "" ? null : lastUnit.trim(),
      });
      toast.success(t("passport.datesSaved"));
      onSaved();
    } catch (e) {
      onError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={t("passport.retention")}
      extra={
        canWrite ? (
          <Button size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
            {t("common.save")}
          </Button>
        ) : undefined
      }
    >
      <Space direction="vertical" size="sm" style={{ width: "100%" }}>
        <Text small secondary>
          {t("passport.retentionHint").replace("{years}", String(retention.retentionYears))}
        </Text>
        <Space wrap>
          <Input
            label={t("passport.placedOnMarket")}
            placeholder="YYYY-MM-DD"
            disabled={!canWrite}
            value={placedOn}
            onChange={(e) => setPlacedOn(e.target.value)}
          />
          <Input
            label={t("passport.lastUnitPlacedOnMarket")}
            placeholder="YYYY-MM-DD"
            disabled={!canWrite}
            value={lastUnit}
            onChange={(e) => setLastUnit(e.target.value)}
          />
        </Space>
        <dl className="divide-y divide-border">
          <MetaRow label={t("passport.retainUntil")}>
            {retention.retainUntil ? fmtDate(retention.retainUntil) : "—"}
          </MetaRow>
          <MetaRow label={t("passport.backup")}>
            {!backup.configured ? (
              <Tag color="warning">{t("passport.backupNotConfigured")}</Tag>
            ) : backup.latest ? (
              backup.latest.status === "failed" && backup.latest.lastError ? (
                <Tooltip title={backup.latest.lastError}>
                  <Tag color="error">{t(`backupStatus.${backup.latest.status}`)}</Tag>
                </Tooltip>
              ) : (
                <Tag color={backup.latest.status === "stored" ? "success" : "warning"}>
                  {t(`backupStatus.${backup.latest.status}`)}
                </Tag>
              )
            ) : (
              <Tag>{t("passport.backupNone")}</Tag>
            )}
          </MetaRow>
          {backup.latest?.storedAt && (
            <MetaRow label={t("passport.backupStoredAt")}>{fmtDate(backup.latest.storedAt)}</MetaRow>
          )}
        </dl>
      </Space>
    </Card>
  );
}
